import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { UserCircleIcon } from "@phosphor-icons/react";
import VerifiedBadge from "@/components/VerifiedBadge";

const timeAgo = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

/**
 * One row in the marquee. Auto-loops by duplicating items so the strip
 * appears infinite. Pauses on hover so the user can read.
 */
function MarqueeRow({ items, role, justVerifiedIds, durationSec = 60, reverse = false, testid }) {
  // Duplicate the list so the keyframes can wrap seamlessly.
  const doubled = useMemo(() => [...items, ...items], [items]);
  if (items.length === 0) return null;

  return (
    <div
      className="bidora-marquee-row"
      data-testid={testid}
      style={{
        // Faster when there are fewer items.
        "--marquee-duration": `${durationSec}s`,
        "--marquee-direction": reverse ? "reverse" : "normal",
      }}
    >
      <div className="bidora-marquee-track">
        {doubled.map((s, idx) => {
          const isNew = justVerifiedIds.has(s.user_id);
          const isSeller = role === "seller";
          const isFounder = !!s.is_founder;
          const segment =
            s.account_type === "wholesale_seller" || s.account_type === "wholesale_buyer"
              ? "Wholesale"
              : "Retail";
          const roleSuffix = isSeller ? "Seller" : "Buyer";
          const Wrapper = isSeller ? Link : "div";
          const wrapperProps = isSeller ? { to: `/seller/${s.user_id}` } : {};
          const founderRowClass = isFounder
            ? isSeller
              ? "bidora-founder-row-seller"
              : "bidora-founder-row-buyer"
            : "bg-white/[0.02]";
          const founderAvatarClass = isFounder
            ? isSeller
              ? "bidora-founder-avatar"
              : "bidora-founder-avatar-buyer"
            : "bidora-diamond-avatar";
          const pillVariantClass = isSeller
            ? "bidora-founder-pill-seller"
            : "bidora-founder-pill-buyer";
          return (
            <Wrapper
              key={`${s.user_id}-${idx}`}
              {...wrapperProps}
              className={`group inline-flex items-center gap-3 px-4 py-2 mr-6 rounded-full ${founderRowClass} border border-white/5 hover:border-[#4FC3F7]/40 hover:bg-white/[0.05] transition shrink-0 ${
                isSeller ? "cursor-pointer" : ""
              } ${isNew ? "verified-just-in" : ""}`}
              data-testid={idx < items.length ? `recently-verified-${s.user_id}` : undefined}
            >
              <div
                className={`${founderAvatarClass} h-10 w-10 rounded-full bg-zinc-800 overflow-hidden shrink-0`}
              >
                {s.picture ? (
                  <img
                    src={s.picture}
                    alt={s.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <UserCircleIcon size={40} className="text-zinc-600" />
                )}
              </div>
              <div className="leading-tight min-w-0">
                {isFounder && (
                  <span
                    className={`inline-flex items-center gap-0.5 px-1 py-[1px] rounded-full text-[7px] font-extrabold uppercase tracking-wider mb-0.5 ${pillVariantClass}`}
                    title={`Founder ${roleSuffix.toLowerCase()} — early supporter`}
                    data-testid={`founder-pill-${s.user_id}`}
                  >
                    ★ Founder
                  </span>
                )}
                <div className="flex items-center gap-1 text-sm font-semibold text-zinc-100 group-hover:text-white">
                  <span className={`shrink-0 ${isNew ? "verified-shine-wrap inline-flex" : "inline-flex"}`}>
                    <VerifiedBadge size={14} />
                  </span>
                  <span className="max-w-[180px] truncate">
                    {/* Privacy: only the public @handle is shown — never real names —
                        so verified accounts can't be doxxed off the homepage. */}
                    {s.username
                      ? `@${s.username}`
                      : (isSeller ? "New seller" : "New buyer")}
                  </span>
                </div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">
                  {segment} · Verified · {roleSuffix}
                  {s.approved_at && (
                    <span className="ml-1.5 text-emerald-400 normal-case tracking-normal">
                      {timeAgo(s.approved_at)}
                    </span>
                  )}
                  {isNew && (
                    <span className="ml-1.5 text-[#20D5EC] normal-case tracking-normal font-bold">
                      · just now
                    </span>
                  )}
                </p>
              </div>
            </Wrapper>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Two-row horizontal marquee:
 *   1. Verified Sellers — scrolls left-to-right
 *   2. Verified Buyers  — scrolls right-to-left (counter-direction adds visual energy)
 *
 * Each avatar carries a diamond-glow halo (CSS in index.css).
 */
export default function VerifiedMarquee({ items = [], justVerifiedIds = new Set() }) {
  const sellers = items.filter((s) => (s.role || s.account_type || "").includes("seller"));
  const buyers = items.filter((s) => !(s.role || s.account_type || "").includes("seller"));

  if (sellers.length === 0 && buyers.length === 0) return null;

  return (
    <div className="mt-8 pt-6 border-t border-zinc-900" data-testid="home-stats-recently-verified">
      {sellers.length > 0 && (
        <>
          <p className="text-[10px] tracking-[0.22em] text-zinc-500 uppercase font-bold mb-3">
            Recently verified · Sellers
          </p>
          <MarqueeRow
            items={sellers}
            role="seller"
            justVerifiedIds={justVerifiedIds}
            durationSec={Math.max(40, sellers.length * 8)}
            testid="marquee-row-sellers"
          />
        </>
      )}

      {buyers.length > 0 && (
        <>
          <p className="text-[10px] tracking-[0.22em] text-zinc-500 uppercase font-bold mb-3 mt-6">
            Recently verified · Buyers
          </p>
          <MarqueeRow
            items={buyers}
            role="buyer"
            justVerifiedIds={justVerifiedIds}
            durationSec={Math.max(40, buyers.length * 8)}
            reverse
            testid="marquee-row-buyers"
          />
        </>
      )}
    </div>
  );
}
